import { ConnectProto } from "@racoon/api";
import { fromTimestamp } from "@racoon/lib";
import type { GpsStop } from "~/features/gps-stops";
import { distanceMeters } from "~/features/map-view/model/geo-distance";

/**
 * Where the TTN's times and places come from per GPS (design 16456:40726):
 *
 * - Выезд         first time the truck leaves the garage (depot)
 * - Возвращение   last time it enters the garage
 * - trip start    first time it leaves the garage or a disposal site within the trip
 * - trip unload   the scale ticket's time, else entering the disposal site within the trip
 *
 * All of it is read off the gps_stop rows: a stop's end is the truck leaving
 * that place, its start the truck arriving.
 */

/** A GPS stop at a known location, reduced to what the TTN takes from it. */
export type GpsEvent = { at: Date; locationId: bigint; lat: number; lon: number };

export type TripGps = {
  /** Left the garage or a disposal site. */
  start?: Date;
  /** Entered the disposal site the trip unloaded at. */
  unload?: GpsEvent;
};

type Window = { from: Date; to: Date };

const HOUR_MS = 3_600_000;
/** Driver buttons lag the truck, so a trip's own window is widened by this. */
const TRIP_SLACK_MS = 30 * 60_000;
/** How far before the shift's start / first trip the garage exit is looked for. */
const DEPARTURE_LOOKBACK_MS = 3 * HOUR_MS;
/** How far after the last trip the garage entry is looked for. */
const ARRIVAL_LOOKAHEAD_MS = 6 * HOUR_MS;

const eventOf = (stop: GpsStop, at: Date): GpsEvent | undefined =>
  stop.locationId ? { at, locationId: stop.locationId, lat: stop.lat, lon: stop.lon } : undefined;

const isGarage = (stop: GpsStop) => stop.type === "DEPOT" && Boolean(stop.locationId);
const isDisposal = (stop: GpsStop) => stop.type === "DISPOSAL" && Boolean(stop.locationId);

/**
 * A disposal site on the same yard as a garage (KWC-Полигон by the KWC depot)
 * comes out of extract_gps_stops as a DEPOT stop — the depot matches first. So
 * a garage stop counts as the unload when it is where the trip was to unload.
 */
const UNLOAD_AT_GARAGE_MAX_M = 1_000;

/** Metres from the stop to the trip's unload point, if the route has one. */
const distanceToDestination = (stop: GpsStop, route: ConnectProto.Route) =>
  route.destinationLat || route.destinationLon
    ? distanceMeters(stop.lat, stop.lon, route.destinationLat, route.destinationLon)
    : undefined;

const isUnloadStop = (stop: GpsStop, route: ConnectProto.Route) => {
  if (isDisposal(stop)) return true;
  if (!isGarage(stop)) return false;
  const distance = distanceToDestination(stop, route);
  return distance !== undefined && distance <= UNLOAD_AT_GARAGE_MAX_M;
};

/** The stop is at the site the trip was to unload at. */
const isAtDestination = (stop: GpsStop, route: ConnectProto.Route) =>
  (Boolean(route.destinationLocationId) && stop.locationId === route.destinationLocationId) ||
  isGarage(stop);

/**
 * The period a trip is searched in: its actual times, else the GPS-corrected
 * ones, else the plan — never the TTN draft, so the GPS values stay put while
 * the dispatcher edits.
 */
export const tripWindowOf = (route: ConnectProto.Route): Window | undefined => {
  const from =
    fromTimestamp(route.factStartAt) ??
    fromTimestamp(route.gpsStartAt) ??
    fromTimestamp(route.planStartAt);
  const to =
    fromTimestamp(route.factEndAt) ??
    fromTimestamp(route.gpsEndAt) ??
    fromTimestamp(route.planEndAt);
  return from && to ? { from, to } : undefined;
};

const countedTrips = (routes: ConnectProto.Route[]) =>
  routes.filter((route) => route.status !== ConnectProto.RouteStatus.CANCELLED);

/**
 * The span the shift's garage exit / entry are searched in: from a few hours
 * before the shift started (or its first trip) to a few hours after its last
 * trip. Bounded by the trips on purpose — a shift closed days late must not
 * pull the return, and the stops request, out to that day.
 */
export const shiftSearchWindow = (
  shift: ConnectProto.DriverOnShift,
  routes: ConnectProto.Route[],
): Window | undefined => {
  const windows = countedTrips(routes).flatMap((route) => tripWindowOf(route) ?? []);
  const startedAt = fromTimestamp(shift.startedAt);
  const starts = [
    ...windows.map((w) => w.from.getTime()),
    ...(startedAt ? [startedAt.getTime()] : []),
  ];
  if (starts.length === 0) return undefined;
  const lastTripEnd = windows.length > 0 ? Math.max(...windows.map((w) => w.to.getTime())) : null;
  const end = lastTripEnd ?? fromTimestamp(shift.completedAt)?.getTime() ?? Math.max(...starts);
  return {
    from: new Date(Math.min(...starts) - DEPARTURE_LOOKBACK_MS),
    to: new Date(end + ARRIVAL_LOOKAHEAD_MS),
  };
};

/** Every trip's GPS start and unload. */
export const deriveTripsGps = (
  routes: ConnectProto.Route[],
  stops: GpsStop[],
): Map<bigint, TripGps> => {
  const byRoute = new Map<bigint, TripGps>();
  // A cancelled trip was never driven: nothing the truck did belongs to it.
  for (const route of countedTrips(routes)) {
    const window = tripWindowOf(route);
    if (!window) continue;
    const from = window.from.getTime() - TRIP_SLACK_MS;
    // The scale ticket is the surest unload time: a trip whose recorded times
    // end before it is still searched up to it.
    const scaleAt = scaleTimeOf(route)?.getTime();
    const to = Math.max(window.to.getTime(), scaleAt ?? 0) + TRIP_SLACK_MS;

    // The trip starts on its first exit from the garage or a disposal site —
    // the previous trip's unload.
    const startStop = stops
      .filter((stop) => {
        const left = stop.endAt.getTime();
        return (isGarage(stop) || isDisposal(stop)) && left >= from && left <= to;
      })
      .sort((a, b) => a.endAt.getTime() - b.endAt.getTime())[0];

    // …and unloads on its first arrival at the unload site after that. First,
    // not last: the window can reach into the next trip, and a later stop at a
    // garage on the same yard (a lunch break) is not a second unload.
    // The trip's own unload site wins over another disposal site: the widened
    // window can reach the next trip's unload elsewhere. Only with no visit to
    // it does another site stand, as where the truck actually unloaded.
    const after = startStop ? startStop.endAt.getTime() : from;
    const unloadStops = stops
      .filter((stop) => {
        const arrived = stop.startAt.getTime();
        return isUnloadStop(stop, route) && arrived > after && arrived <= to;
      })
      .sort((a, b) => a.startAt.getTime() - b.startAt.getTime());
    const unloadStop = unloadStops.find((stop) => isAtDestination(stop, route)) ?? unloadStops[0];
    // A garage stop confirms the route's own unload site rather than naming
    // the depot as the place.
    const unload =
      unloadStop &&
      (isDisposal(unloadStop)
        ? eventOf(unloadStop, unloadStop.startAt)
        : route.destinationLocationId
          ? {
              at: unloadStop.startAt,
              locationId: route.destinationLocationId,
              lat: route.destinationLat,
              lon: route.destinationLon,
            }
          : undefined);

    if (startStop || unload) byRoute.set(route.id, { start: startStop?.endAt, unload });
  }
  return byRoute;
};

/** The shift's first garage exit and last garage entry. */
export const deriveShiftGps = (
  window: Window | undefined,
  stops: GpsStop[],
): { departure?: GpsEvent; arrival?: GpsEvent } => {
  if (!window) return {};
  const from = window.from.getTime();
  const to = window.to.getTime();
  const garages = stops.filter(isGarage);

  const departureStop = garages
    .filter((stop) => stop.endAt.getTime() >= from && stop.endAt.getTime() <= to)
    .sort((a, b) => a.endAt.getTime() - b.endAt.getTime())[0];
  const departure = departureStop && eventOf(departureStop, departureStop.endAt);

  // Only entries after the truck left: the overnight stop it left from is a
  // garage entry too, from the evening before.
  const after = departure ? departure.at.getTime() : from;
  const arrivalStop = garages
    .filter((stop) => stop.startAt.getTime() > after && stop.startAt.getTime() <= to)
    .sort((a, b) => a.startAt.getTime() - b.startAt.getTime())
    .pop();
  const arrival = arrivalStop && eventOf(arrivalStop, arrivalStop.startAt);

  return { departure, arrival };
};

/** When the trip's load was weighed at the disposal site, per its scale ticket. */
export const scaleTimeOf = (route: ConnectProto.Route) =>
  fromTimestamp(route.scaleRecord?.bruttoScaleTs) ?? fromTimestamp(route.scaleRecord?.nettoScaleTs);
